import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { IMPACT_FORMS, PUBLICATIONS } from "@/data/content";
import PageHero from "@/components/site/PageHero";
import LogoWall from "@/components/site/LogoWall";
import CTABand from "@/components/site/CTABand";

export default function Impact() {
  useDocumentTitle("Impact | Collaboration on trustworthy technology — Meta61");
  const categories = ["All", ...PUBLICATIONS.map((p) => p.category)];
  const [filter, setFilter] = useState("All");
  const groups = filter === "All" ? PUBLICATIONS : PUBLICATIONS.filter((p) => p.category === filter);

  return (
    <div>
      <PageHero
        title="Where we make a"
        accent="difference."
        subhead="Meta61's impact comes through collaboration — contributing to standards, advising institutions, and producing the research that moves the field forward."
      />

      {/* How we collaborate */}
      <section className="m61-section">
        <div className="m61-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><p className="m61-eyebrow mb-5">How we collaborate</p></div>
          <div className="lg:col-span-8">
            <p className="text-lg text-m61-muted leading-relaxed mb-8">
              We work with the institutions that set the agenda for trustworthy technology, and with
              the enterprises and governments putting it into practice. Our contribution typically
              takes one of four forms:
            </p>
            <div className="grid grid-cols-2 gap-px bg-m61-line border border-m61-line">
              {IMPACT_FORMS.map((f, i) => (
                <div key={f} className="bg-m61-surface p-6">
                  <span className="font-mono text-xs text-m61-muted">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-m61-text font-display mt-2">{f}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="m61-section border-t border-m61-line bg-m61-surface/40">
        <div className="m61-container">
          <p className="m61-eyebrow mb-6">Publications &amp; research</p>
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  filter === c ? "border-m61-cyan text-m61-cyan bg-m61-cyan/10" : "border-m61-line text-m61-muted hover:border-m61-line-strong"
                }`}
                data-testid={`impact-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="space-y-12">
            {groups.map((g) => (
              <div key={g.category}>
                <h3 className="m61-h3 mb-5 text-m61-cyan">{g.category}</h3>
                <ol className="space-y-4">
                  {g.items.map((item, i) => (
                    <li key={i} className="border-l-2 border-m61-line-strong pl-5">
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="group block">
                        <p className="text-m61-muted leading-relaxed group-hover:text-m61-text transition-colors">{item.text}</p>
                        <span className="text-m61-cyan text-sm inline-flex items-center gap-1.5 mt-2 break-all">
                          View source <ArrowUpRight className="w-3.5 h-3.5 shrink-0" />
                        </span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LogoWall heading="Trusted engagements across our capability space" />

      <CTABand />
    </div>
  );
}
